import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { RotateCcw, Activity, AlertTriangle, MessageCircle, FlagTriangleRight } from "lucide-react";
import { registrationPerformanceGame } from "@/games/registration-performance/content";
import {
  startGame,
  choose as chooseAction,
  isOver,
  resolveOutcome,
  type GameState,
  type EventKind,
  type Effect,
} from "@/games/registration-performance/engine";

type Screen = "landing" | "playing" | "done";

const SENDER_AVATAR: Record<string, string> = {
  Registrar: "🧑‍🎓",
  DBA: "🗄️",
  "Vendor Account Manager": "🤝",
  Leadership: "🏛️",
  "QA Lead": "🧪",
  Security: "🛡️",
  "UAT Team": "✅",
  "Steering Committee": "🧑‍⚖️",
  "On-Call Engineer": "🚨",
  Director: "🧑‍💼",
};

const METRIC_ICON: Record<string, string> = {
  scopeStability: "🧭",
  scheduleConfidence: "⏱️",
  dataIntegrity: "🗃️",
  stakeholderTrust: "🤝",
  controlReadiness: "🛡️",
  operationalReadiness: "⚙️",
};

const KIND_STYLE: Record<EventKind, { label: string; icon: JSX.Element; className: string }> = {
  decision: {
    label: "Decision",
    icon: <MessageCircle className="h-3 w-3" />,
    className: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
  },
  risk: {
    label: "Risk Event",
    icon: <AlertTriangle className="h-3 w-3" />,
    className: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  },
  gate: {
    label: "Gate",
    icon: <FlagTriangleRight className="h-3 w-3" />,
    className: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  },
};

function humanize(key: string): string {
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

function healthColor(value: number): string {
  return value >= 65 ? "bg-emerald-500" : value >= 40 ? "bg-amber-500" : "bg-red-500";
}

function MetricStrip({ metrics }: { metrics: Record<string, number> }) {
  return (
    <div className="flex flex-wrap gap-3 py-3 border-b border-border">
      {Object.entries(metrics).map(([key, value]) => (
        <div
          key={key}
          className="flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1"
        >
          <span className="text-sm leading-none">{METRIC_ICON[key] ?? "📈"}</span>
          <span
            className={`h-2 w-2 rounded-full ${healthColor(value)} ${value < 40 ? "animate-pulse" : ""}`}
          />
          <span className="text-[11px] font-body text-muted-foreground">{humanize(key)}</span>
          <span className="text-[11px] font-mono text-foreground">{value}</span>
        </div>
      ))}
    </div>
  );
}

function DeltaChips({ effects }: { effects: Effect[] }) {
  return (
    <div className="flex flex-wrap gap-1.5 mb-5">
      {effects.map((e, i) => (
        <span
          key={i}
          className={`inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full ${
            e.delta >= 0
              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : "bg-red-500/10 text-red-500"
          }`}
        >
          <span>{METRIC_ICON[e.metric] ?? "📈"}</span>
          {humanize(e.metric)} {e.delta >= 0 ? "+" : ""}
          {e.delta}
        </span>
      ))}
    </div>
  );
}

export default function RegistrationSimGame() {
  const game = registrationPerformanceGame;
  const [screen, setScreen] = useState<Screen>("landing");
  const [state, setState] = useState<GameState>(() => startGame(game));
  const [lastEffects, setLastEffects] = useState<Effect[] | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const activeEvent = useMemo(
    () => game.events.find((e) => e.id === state.activeEventIds[0]),
    [game, state.activeEventIds]
  );

  const start = () => {
    setState(startGame(game));
    setLastResult(null);
    setLastEffects(null);
    setScreen("playing");
  };

  const restart = () => {
    setState(startGame(game));
    setLastResult(null);
    setLastEffects(null);
    setScreen("landing");
  };

  const pick = (choiceId: string) => {
    if (!activeEvent) return;
    const choice = activeEvent.choices.find((c) => c.id === choiceId);
    const next = chooseAction(game, state, activeEvent.id, choiceId);
    setLastResult(choice?.resultText ?? null);
    setLastEffects(choice?.effects ?? null);
    setState(next);
    if (isOver(game, next)) setScreen("done");
  };

  if (screen === "landing") {
    return (
      <div className="p-6 lg:p-8 max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-4xl leading-none">🎓</span>
          <h1 className="text-xl font-display font-bold text-foreground">{game.title}</h1>
        </div>
        <div className="space-y-2 mb-6">
          {game.premise.map((line, i) => (
            <p key={i} className="text-sm font-body text-muted-foreground leading-relaxed">
              {line}
            </p>
          ))}
        </div>
        <Button onClick={start}>Start</Button>
      </div>
    );
  }

  if (screen === "done") {
    const outcome = resolveOutcome(game, state);
    return (
      <div className="p-6 lg:p-8 max-w-xl mx-auto">
        <div className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-3 py-1.5 mb-4">
          <span className="text-base leading-none">🏁</span>
          <span className="text-xs font-semibold font-body">{outcome.label}</span>
        </div>
        <p className="text-sm font-body text-card-foreground leading-relaxed mb-6">{outcome.summary}</p>

        <p className="text-xs font-semibold font-body text-foreground mb-2">What happened</p>
        <div className="space-y-2 mb-6">
          {state.history.map((h, i) => (
            <p key={i} className="text-xs font-body text-muted-foreground">
              <span className="font-mono text-[10px] text-muted-foreground/70">Day {h.day} · </span>
              {h.choiceText}
            </p>
          ))}
        </div>

        <Button onClick={restart}>
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          Play again
        </Button>
      </div>
    );
  }

  const kindStyle = activeEvent ? KIND_STYLE[activeEvent.kind] : null;

  return (
    <div className="p-6 lg:p-8 max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-accent" />
          <span className="text-sm font-display font-bold text-foreground">{game.title}</span>
        </div>
        <button onClick={restart} className="text-[11px] text-muted-foreground hover:text-foreground font-body">
          Restart
        </button>
      </div>

      <MetricStrip metrics={state.metrics} />

      {activeEvent && (
        <div className="pt-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[11px] font-mono text-muted-foreground">{activeEvent.day}</span>
            {kindStyle && (
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-semibold font-body px-2 py-0.5 rounded-full ${kindStyle.className}`}
              >
                {kindStyle.icon}
                {kindStyle.label}
              </span>
            )}
          </div>

          <div className="flex items-start gap-3 mb-5">
            <span className="shrink-0 h-11 w-11 rounded-full bg-muted flex items-center justify-center text-2xl leading-none border border-border">
              {SENDER_AVATAR[activeEvent.sender] ?? "👤"}
            </span>
            <div className="flex-1">
              <p className="text-xs font-semibold font-body text-foreground mb-1">{activeEvent.sender}</p>
              <div className="relative rounded-2xl rounded-tl-sm bg-muted/60 border border-border px-4 py-3 space-y-2">
                <p className="text-sm font-display font-bold text-foreground">{activeEvent.title}</p>
                {activeEvent.body.map((line, i) => (
                  <p key={i} className="text-sm font-body text-card-foreground leading-relaxed">
                    {line}
                  </p>
                ))}
              </div>
            </div>
          </div>

          {lastResult && (
            <div className="mb-2 ml-14 rounded-xl bg-accent/5 border border-accent/30 px-3.5 py-2.5">
              <p className="text-xs font-body text-muted-foreground italic">{lastResult}</p>
            </div>
          )}
          {lastEffects && (
            <div className="ml-14">
              <DeltaChips effects={lastEffects} />
            </div>
          )}

          <div className="space-y-2">
            {activeEvent.choices.map((choice) => (
              <button
                key={choice.id}
                onClick={() => pick(choice.id)}
                className="w-full text-left border border-border rounded-xl px-4 py-3 text-sm font-body text-card-foreground hover:border-accent hover:bg-accent/5 hover:scale-[1.01] transition-all"
              >
                {choice.text}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
